import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Columns3,
  Database,
  Download,
  Eye,
  FileArchive,
  FileJson,
  FileUp,
  Filter,
  FolderOpen,
  Gauge,
  GitMerge,
  GitCompareArrows,
  Grid3X3,
  HardDrive,
  Info,
  Keyboard,
  Layers3,
  Loader2,
  Menu,
  Moon,
  PanelRight,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Save,
  Search,
  Scissors,
  Send,
  Settings,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Sparkles,
  Square,
  Tags,
  UploadCloud,
  Undo2,
  Wand2,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuraTelemetryEventLog, ProoflineStatus } from "@auraone/aura-ide-kit";
import { commands, datasets, probeTrials } from "./data";
import {
  buildVirtualWindow,
  clampTime,
  defaultFilters,
  defaultSort,
  exportManifest,
  formatTime,
  nextSuccessState,
  readinessTone,
  summarizeDataset,
  visibleEpisodes,
} from "./core";
import {
  artifactPlan,
  buildDeterministicClusters,
  buildLocalEvidenceArchive,
  buildProbeReport,
  buildSensorQaReport,
  buildSensorQaMarkdown,
  createDatasetFromLocalFiles,
  downloadBinaryArtifact,
  downloadTextArtifact,
  mergeClusterRows,
  recomputeDeterministicClusters,
  refreshClusterRows,
  splitClusterRows,
  summarizeClusterRows,
  type ExportScope,
  type LocalFileEntry,
} from "./localOperations";
import {
  LocalDiagnosticEventBuffer,
  ROBOTICS_PLATFORM_HOOKS,
  createRoboticsDiagnosticEvent,
  ensureRoboticsIntakeInstallSigningKeypair,
  roboticsIntakeInstallSigningKeypairKey,
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  Cluster,
  CommandId,
  DatasetTab,
  Density,
  Episode,
  ExportTarget,
  FilterState,
  Intervention,
  SensorStream,
  SortField,
  SortState,
  ViewMode,
} from "./types";

import {
  Chip,
  ManifestMetadataScene,
  Sparkline,
  SyntheticSensorScene,
  humanTaskTitle,
} from "./components";
import { SensorPreview, ViewContext, interventionTone } from "./views-browse";
import type { ViewBody } from "./views-release";
export function ScrubView({
  dataset,
  selectedEpisode,
  cursor,
  setCursor,
  playing,
  setPlaying,
  speed,
  setSpeed,
  updateEpisode,
  activeSensorId,
  setActiveSensorId,
}: Pick<
  Parameters<typeof ViewBody>[0],
  | "dataset"
  | "selectedEpisode"
  | "cursor"
  | "setCursor"
  | "playing"
  | "setPlaying"
  | "speed"
  | "setSpeed"
  | "updateEpisode"
  | "activeSensorId"
  | "setActiveSensorId"
>) {
  const visualSensors = selectedEpisode.sensors.filter(
    (sensor) =>
      sensor.visible && (sensor.kind === "rgb" || sensor.kind === "depth"),
  );
  const telemetrySensors = selectedEpisode.sensors.filter(
    (sensor) =>
      sensor.visible &&
      sensor.kind !== "rgb" &&
      sensor.kind !== "depth" &&
      sensor.kind !== "language",
  );
  const activeSensor =
    visualSensors.find((sensor) => sensor.id === activeSensorId) ??
    visualSensors[0];
  const taskTitle = humanTaskTitle(selectedEpisode.task);
  const synthetic = dataset.provenance.kind === "repository-synthetic-fixture";
  const duration = selectedEpisode.duration;
  const frameStep = selectedEpisode.frameRateHz
    ? 1 / selectedEpisode.frameRateHz
    : 0.01;
  return (
    <section
      className="surface scrub-surface"
      aria-label="Episode scrubber"
      tabIndex={0}
    >
      <div className="review-heading">
        <div className="review-title">
          <span className="eyebrow">Evidence review</span>
          <h1>{taskTitle}</h1>
          <p>
            <code>{selectedEpisode.id}</code>
            <span>{selectedEpisode.embodiment ?? "Embodiment unknown"}</span>
            <span>{formatTime(duration)}</span>
          </p>
        </div>
        <div
          className="decision-actions"
          role="group"
          aria-label="Episode decision"
        >
          <button
            className={`decision-button outcome-${selectedEpisode.success}`}
            onClick={() =>
              updateEpisode(selectedEpisode.id, {
                success: nextSuccessState(selectedEpisode.success),
              })
            }
          >
            <BadgeCheck size={16} />
            <span>Outcome</span>
            <strong>{selectedEpisode.success}</strong>
          </button>
          <button
            className={
              selectedEpisode.reviewed === "reviewed"
                ? "decision-button confirmed"
                : "decision-button"
            }
            onClick={() =>
              updateEpisode(selectedEpisode.id, {
                reviewed:
                  selectedEpisode.reviewed === "reviewed"
                    ? "unreviewed"
                    : "reviewed",
              })
            }
          >
            <CheckCircle2 size={16} />
            <span>Decision</span>
            <strong>
              {selectedEpisode.reviewed === "reviewed"
                ? "Reviewed"
                : "Mark reviewed"}
            </strong>
          </button>
        </div>
      </div>
      <div className="review-cockpit">
        <div className="evidence-column">
          <figure
            className={`evidence-viewport ${activeSensor?.kind ?? "unknown"}`}
            aria-label={
              activeSensor
                ? `${activeSensor.label} ${synthetic ? "synthetic evidence" : "imported manifest metadata"} viewport`
                : "No visual sensor evidence declared in imported manifest"
            }
          >
            {activeSensor ? (
              synthetic ? (
                <SyntheticSensorScene sensorId={activeSensor.id} />
              ) : (
                <ManifestMetadataScene sensor={activeSensor} />
              )
            ) : (
              <ManifestMetadataScene />
            )}
            <figcaption>
              <span className="synthetic-label">
                {synthetic
                  ? "Synthetic repository evidence"
                  : "Imported manifest metadata"}
              </span>
              <strong>
                {activeSensor?.label ?? "No visual sensor evidence"}
              </strong>
              <span>
                {activeSensor?.rateHz === null || activeSensor === undefined
                  ? "Rate unknown"
                  : `${activeSensor.rateHz} Hz`}
                {selectedEpisode.frameRateHz
                  ? ` · frame ${Math.round(cursor * selectedEpisode.frameRateHz)}`
                  : ""}
                {duration === null ? "" : ` · ${formatTime(cursor)}`}
              </span>
            </figcaption>
          </figure>
          {visualSensors.length ? (
            <div
              className="sensor-selector"
              role="tablist"
              aria-label="Evidence sensors"
            >
              {visualSensors.map((sensor) => (
                <button
                  key={sensor.id}
                  role="tab"
                  aria-label={`${sensor.label}, ${sensor.kind === "depth" ? "depth map" : "RGB camera"}, health ${sensor.status}`}
                  aria-selected={sensor.id === activeSensor?.id}
                  className={
                    sensor.id === activeSensor?.id
                      ? "sensor-option active"
                      : "sensor-option"
                  }
                  onClick={() => setActiveSensorId(sensor.id)}
                >
                  <span className={`sensor-thumb ${sensor.kind}`}>
                    {synthetic ? (
                      <SyntheticSensorScene sensorId={sensor.id} compact />
                    ) : (
                      <ManifestMetadataScene sensor={sensor} compact />
                    )}
                  </span>
                  <span>
                    <strong>
                      {sensor.label.replace("RGB ", "").replace("Depth ", "")}
                    </strong>
                    <small>
                      {sensor.kind === "depth" ? "Depth map" : "RGB camera"} ·{" "}
                      {sensor.status}
                    </small>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="sensor-empty" role="status">
              No RGB or depth sensor records were declared in this manifest.
            </div>
          )}
        </div>
        <aside className="evidence-context" aria-label="Language and telemetry">
          <section className="instruction-card">
            <span>Language instruction</span>
            <p>
              {selectedEpisode.instruction ??
                "Unknown; no instruction field was imported."}
            </p>
          </section>
          <section className="telemetry-stack">
            <div className="telemetry-heading">
              <strong>Telemetry</strong>
              <span>cursor aligned</span>
            </div>
            {telemetrySensors.length ? (
              telemetrySensors.map((sensor, index) => (
                <article
                  key={sensor.id}
                  className={`telemetry-row ${sensor.kind}`}
                >
                  <div>
                    <strong>{sensor.label}</strong>
                    <span>
                      {sensor.rateHz === null
                        ? "Rate unknown"
                        : `${sensor.rateHz} Hz`}{" "}
                      · {sensor.status}
                    </span>
                  </div>
                  {sensor.samples.length ? (
                    <Sparkline
                      values={sensor.samples}
                      accentKey={sensor.kind}
                      fallbackIndex={index}
                    />
                  ) : (
                    <span className="telemetry-unknown">
                      No samples declared
                    </span>
                  )}
                </article>
              ))
            ) : (
              <p className="telemetry-unknown">
                No non-visual sensor records were declared.
              </p>
            )}
          </section>
        </aside>
      </div>
      <div className="review-dock" data-testid="review-dock">
        <div className="transport">
          <button
            disabled={duration === null}
            title={
              duration === null
                ? "No duration was declared for this imported episode."
                : undefined
            }
            onClick={() =>
              setCursor((value) => clampTime(value - frameStep, duration))
            }
            aria-label="Previous frame"
          >
            <SkipBack size={16} />
          </button>
          <button
            disabled={duration === null}
            title={
              duration === null
                ? "Playback requires an imported duration."
                : undefined
            }
            className="play-button"
            onClick={() => setPlaying((current) => !current)}
            aria-label={playing ? "Pause episode" : "Play episode"}
          >
            {playing ? <Square size={16} /> : <Play size={16} />}
          </button>
          <button
            disabled={duration === null}
            title={
              duration === null
                ? "No duration was declared for this imported episode."
                : undefined
            }
            onClick={() =>
              setCursor((value) => clampTime(value + frameStep, duration))
            }
            aria-label="Next frame"
          >
            <SkipForward size={16} />
          </button>
          <strong data-testid="playback-time">
            {duration === null ? "Time unknown" : formatTime(cursor)}{" "}
            <span>/ {formatTime(duration)}</span>
          </strong>
          <select
            aria-label="Playback speed"
            value={speed}
            onChange={(event) => setSpeed(Number(event.target.value))}
          >
            {[0.1, 0.25, 0.5, 1, 2, 4, 8].map((option) => (
              <option key={option} value={option}>
                {option}x
              </option>
            ))}
          </select>
        </div>
        <div
          className="timeline"
          role="group"
          aria-label="Synchronized episode timeline"
        >
          <div className="phase-strip">
            {duration === null
              ? null
              : selectedEpisode.phases.map((phase) =>
                  phase.start === null || phase.end === null ? null : (
                    <span
                      key={phase.id}
                      style={{
                        left: `${(phase.start / duration) * 100}%`,
                        width: `${((phase.end - phase.start) / duration) * 100}%`,
                      }}
                    >
                      {phase.label}
                    </span>
                  ),
                )}
          </div>
          <div className="marker-strip">
            {duration === null
              ? null
              : selectedEpisode.interventions.map((intervention) =>
                  intervention.start === null ||
                  intervention.end === null ? null : (
                    <span
                      key={intervention.id}
                      className={`marker tone-${interventionTone(intervention.why)}`}
                      aria-hidden="true"
                      title={`${intervention.why}: ${intervention.outcome}`}
                      style={{
                        left: `${(intervention.start / duration) * 100}%`,
                        width: `${Math.max(1.5, ((intervention.end - intervention.start) / duration) * 100)}%`,
                      }}
                    />
                  ),
                )}
            {duration === null
              ? null
              : selectedEpisode.anomalies.map((anomaly) =>
                  anomaly.start === null ? null : (
                    <i
                      key={anomaly.id}
                      className={`anomaly tone-${anomaly.severity === "error" ? "fail" : anomaly.severity === "warn" ? "warn" : "info"}`}
                      style={{ left: `${(anomaly.start / duration) * 100}%` }}
                      title={anomaly.note}
                    />
                  ),
                )}
          </div>
          <input
            aria-label="Frame-accurate scrubber"
            type="range"
            min={0}
            max={duration ?? 0}
            value={cursor}
            step="any"
            disabled={duration === null}
            onChange={(event) => setCursor(Number(event.target.value))}
          />
        </div>
      </div>
      <details className="accessible-data">
        <summary>Timeline records</summary>
        <div
          className="table-scroll"
          role="region"
          aria-label="Ordered timeline records"
          tabIndex={0}
        >
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Record</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {selectedEpisode.phases.map((phase) => (
                <tr key={phase.id}>
                  <td>
                    {formatTime(phase.start)}-{formatTime(phase.end)}
                  </td>
                  <td>Phase</td>
                  <td>{phase.label}</td>
                  <td>Sequence</td>
                </tr>
              ))}
              {selectedEpisode.interventions.map((intervention) => (
                <tr key={intervention.id}>
                  <td>
                    {formatTime(intervention.start)}-
                    {formatTime(intervention.end)}
                  </td>
                  <td>Intervention</td>
                  <td>{intervention.why}</td>
                  <td>{intervention.outcome}</td>
                </tr>
              ))}
              {selectedEpisode.anomalies.map((anomaly) => (
                <tr key={anomaly.id}>
                  <td>
                    {formatTime(anomaly.start)}-{formatTime(anomaly.end)}
                  </td>
                  <td>{anomaly.severity}</td>
                  <td>{anomaly.sensorId}</td>
                  <td>{anomaly.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <ReviewEditors episode={selectedEpisode} />
    </section>
  );
}


export function ReviewEditors({ episode }: { episode: Episode }) {
  return (
    <div className="review-editors">
      <article>
        <h2>
          <Tags size={16} /> Failure taxonomy
        </h2>
        <div className="chips">
          {episode.taxonomyTags.length ? (
            episode.taxonomyTags.map((tag) => (
              <Chip key={tag} label={tag} tone="warn" />
            ))
          ) : (
            <Chip label="T opens keyboard taxonomy picker" />
          )}
        </div>
      </article>
      <article>
        <h2>
          <AlertTriangle size={16} /> Intervention tags
        </h2>
        {episode.interventions.map((intervention) => (
          <p key={intervention.id}>
            <strong>{formatTime(intervention.start)}</strong> {intervention.why}
            ; {intervention.outcome}
          </p>
        ))}
      </article>
      <article>
        <h2>
          <FileJson size={16} /> Anomaly notes
        </h2>
        {episode.anomalies.map((anomaly) => (
          <p key={anomaly.id}>
            <strong>{anomaly.sensorId}</strong> {anomaly.severity}:{" "}
            {anomaly.note}
          </p>
        ))}
      </article>
    </div>
  );
}
